import React, { useCallback, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { procedureService } from '@/services/procedureService';
import { Procedure, ProcedureData } from '@/types/procedure.types';
import { Patient } from '@/types/patient.types';
import { ProcedureForm } from './ProcedureForm';
import { Plus, Edit, Trash2, AlertCircle, Loader2, Calendar, Stethoscope } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';

interface PatientProceduresSectionProps {
  patient: Patient;
  isReadOnly?: boolean;
}

const formatOutcome30d = (value: Procedure['outcome_30d']): { label: string; variant: 'default' | 'destructive' | 'secondary' } => {
  if (value === null || value === undefined || value === '') {
    return { label: '-', variant: 'secondary' };
  }

  if (typeof value === 'number') {
    if (value === 1) return { label: 'Supraviețuitor', variant: 'default' };
    if (value === 2) return { label: 'Decedat', variant: 'destructive' };
    return { label: 'Necunoscut', variant: 'secondary' };
  }

  const lower = value.toLowerCase();
  if (lower.includes('deceda')) return { label: value, variant: 'destructive' };
  if (lower.includes('suprav') || lower.includes('survivor')) return { label: value, variant: 'default' };
  return { label: value, variant: 'secondary' };
};

export const PatientProceduresSection: React.FC<PatientProceduresSectionProps> = ({
  patient,
  isReadOnly = false,
}) => {
  const { toast } = useToast();
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState<Procedure | null>(null);

  const loadProcedures = useCallback(async () => {
    if (!patient.id) return;
    try {
      setIsLoading(true);
      const response = await procedureService.getProcedures({ patient_id: patient.id, limit: 1000 });
      setProcedures(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error('Error loading procedures:', error);
      toast({
        title: 'Eroare',
        description: error.message || 'Nu s-au putut încărca procedurile.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [patient.id, toast]);

  useEffect(() => {
    loadProcedures();
  }, [loadProcedures]);

  const handleAddProcedure = () => {
    if (isReadOnly) return;
    setEditingProcedure(null);
    setIsDialogOpen(true);
  };

  const handleEditProcedure = (procedure: Procedure) => {
    if (isReadOnly) return;
    setEditingProcedure(procedure);
    setIsDialogOpen(true);
  };

  const handleDeleteProcedure = async (procedure: Procedure) => {
    if (isReadOnly || !procedure.id) return;

    if (!window.confirm('Sigur doriți să ștergeți această procedură?')) {
      return;
    }

    try {
      await procedureService.deleteProcedure(procedure.id);
      toast({
        title: 'Succes',
        description: 'Procedura a fost ștearsă cu succes.',
      });
      await loadProcedures();
    } catch (error: any) {
      toast({
        title: 'Eroare',
        description: error.message || 'Nu s-a putut șterge procedura.',
        variant: 'destructive',
      });
    }
  };

  const handleProcedureSubmit = async (data: ProcedureData) => {
    try {
      if (editingProcedure?.id) {
        await procedureService.updateProcedure(editingProcedure.id, data);
      } else {
        await procedureService.createProcedure(data);
      }
      setIsDialogOpen(false);
      setEditingProcedure(null);
      await loadProcedures();
    } catch (error: any) {
      throw error; // Let ProcedureForm handle the error display
    }
  };

  const columns = [
    {
      name: 'Data',
      selector: (row: Procedure) => row.procedure_date,
      sortable: true,
      width: '120px',
      cell: (row: Procedure) => (
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm">
            {row.procedure_date ? format(parseISO(row.procedure_date), 'dd.MM.yyyy', { locale: ro }) : '-'}
          </span>
        </div>
      ),
    },
    {
      name: 'Tip',
      selector: (row: Procedure) => row.procedure_type_name || '',
      sortable: true,
      width: '160px',
      cell: (row: Procedure) => (
        <Badge variant="secondary" className="text-xs">
          {row.procedure_type_name || '-'}
        </Badge>
      ),
    },
    {
      name: 'Procedură',
      selector: (row: Procedure) => row.procedure_name || '',
      sortable: true,
      grow: 2,
      wrap: true,
      cell: (row: Procedure) => <span className="text-sm">{row.procedure_name || '-'}</span>,
    },
    {
      name: 'Rezultat 30 zile',
      selector: (row: Procedure) => row.outcome_30d ?? '',
      sortable: false,
      width: '150px',
      cell: (row: Procedure) => {
        const { label, variant } = formatOutcome30d(row.outcome_30d);
        return (
          <Badge variant={variant} className="text-xs">
            {label}
          </Badge>
        );
      },
    },
    {
      name: 'Acțiuni',
      button: true,
      width: '120px',
      cell: (row: Procedure) => (
        !isReadOnly && (
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleEditProcedure(row)}
              className="h-8 w-8 p-0"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteProcedure(row)}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )
      ),
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: '600',
        backgroundColor: '#f8fafc',
        color: '#475569',
        paddingTop: '12px',
        paddingBottom: '12px',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
  };

  if (!patient.id) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <Stethoscope className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Pacient nou</h3>
            <p>Salvați mai întâi pacientul pentru a adăuga proceduri.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Stethoscope className="h-5 w-5 text-blue-600" />
            Proceduri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Se încarcă procedurile...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Stethoscope className="h-5 w-5 text-blue-600" />
              Proceduri
              {procedures.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {procedures.length} {procedures.length === 1 ? 'procedură' : 'proceduri'}
                </Badge>
              )}
            </CardTitle>
            {!isReadOnly && (
              <Button type="button" onClick={handleAddProcedure}>
                <Plus className="h-4 w-4 mr-2" />
                Adaugă procedură
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {procedures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                Nu există proceduri
              </h3>
              <p className="text-muted-foreground mb-4">
                Nu au fost adăugate încă proceduri pentru acest pacient.
              </p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={procedures}
              customStyles={customStyles}
              highlightOnHover
              responsive
              noDataComponent={
                <div className="text-center py-4 text-muted-foreground">
                  Nu există proceduri de afișat
                </div>
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Procedure Form Dialog - the portal keeps the DOM <form> out of the patient
          <form>, but React still bubbles the synthetic submit event through the portal
          to the patient form, so it is stopped here. */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingProcedure ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {editingProcedure ? 'Editează procedură' : 'Adaugă procedură'}
            </DialogTitle>
          </DialogHeader>
          <div onSubmit={(e) => e.stopPropagation()}>
            <ProcedureForm
              patient={patient}
              procedure={editingProcedure}
              onSubmit={handleProcedureSubmit}
              onCancel={() => {
                setIsDialogOpen(false);
                setEditingProcedure(null);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
